import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { useAvailability } from "@/lib/availability";
import { useCart } from "@/lib/cart";
import { useCatalog } from "@/lib/catalog-context";
import { config } from "@/lib/config";
import "./ProductPage.css";

export function ProductPage() {
  const { slug } = useParams<{ slug: string }>();
  const { findProduct } = useCatalog();
  const product = slug ? findProduct(slug) : undefined;
  const [selectedTier, setSelectedTier] = useState(0);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [selectedFlavor, setSelectedFlavor] = useState("");
  const { addItem } = useCart();
  const { productHasShopStock, isFlavorAvailable } = useAvailability();

  if (!product) {
    return (
      <div className="pdp">
        <div className="pdp__container">
          <Link to="/" className="pdp__back">
            &larr; Back to menu
          </Link>
          <h1 className="pdp__title">Product not found</h1>
          <p className="pdp__empty">
            We couldn&rsquo;t find that product. It may have been removed or renamed.
          </p>
        </div>
      </div>
    );
  }

  const soldOut = !productHasShopStock(product.slug, product.flavors);
  const tier = product.tiers[selectedTier];
  const hasFlavors = Boolean(product.flavors && product.flavors.length > 0);
  const inStockFlavors =
    product.flavors?.filter((f) => isFlavorAvailable(product.slug, f)) ?? [];
  const needsFlavor = hasFlavors && inStockFlavors.length > 0 && !selectedFlavor;

  useEffect(() => {
    if (!selectedFlavor || !product.flavors?.length) return;
    if (!isFlavorAvailable(product.slug, selectedFlavor)) {
      setSelectedFlavor("");
    }
  }, [product.slug, product.flavors, selectedFlavor, isFlavorAvailable]);
  const priceRange =
    product.tiers.length > 1
      ? `${product.tiers[0].price} \u2013 ${product.tiers[product.tiers.length - 1].price}`
      : product.tiers[0].price;

  function handleAddToCart() {
    addItem({
      slug: product!.slug,
      name: product!.name,
      tier,
      qty,
      imageUrl: product!.imageUrl,
      flavor: selectedFlavor || undefined,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  }

  return (
    <>
      <Helmet>
        <title>
          {product.name} – {config.appName}
        </title>
        <meta
          name="description"
          content={`${product.name} — ${priceRange}. Order via text at ${config.contactPhoneDisplay}.`}
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: product.name,
            image: product.imageUrl ? `${config.publicSiteUrl}${product.imageUrl}` : undefined,
            description: `${product.name} — ${priceRange}`,
            brand: { "@type": "Brand", name: config.appName },
            offers: product.tiers.map((t) => ({
              "@type": "Offer",
              name: t.qty,
              price: t.price.replace("$", ""),
              priceCurrency: "USD",
              availability: soldOut
                ? "https://schema.org/OutOfStock"
                : "https://schema.org/InStock",
            })),
          })}
        </script>
      </Helmet>

      <div className="pdp">
        <div className="pdp__container">
          <Link to="/" className="pdp__back">
            &larr; Back to menu
          </Link>

          <div className="pdp__layout">
            <div className="pdp__image-col">
              {product.imageUrl ? (
                <img
                  src={product.imageUrl}
                  alt={product.name}
                  className="pdp__image"
                  decoding="async"
                />
              ) : (
                <div className="pdp__image-placeholder">
                  <span aria-hidden="true">&#x1F33F;</span>
                </div>
              )}
            </div>

            <div className="pdp__info-col">
              <h1 className="pdp__title">{product.name}</h1>
              <p className="pdp__price-range">{priceRange}</p>

              <div className="pdp__options">
                <label className="pdp__options-label" htmlFor="tier-select">
                  Options
                </label>
                <select
                  id="tier-select"
                  className="pdp__select"
                  value={selectedTier}
                  onChange={(e) => setSelectedTier(Number(e.target.value))}
                >
                  {product.tiers.map((t, i) => (
                    <option key={t.qty} value={i}>
                      {t.qty} &mdash; {t.price}
                    </option>
                  ))}
                </select>
              </div>

              {hasFlavors && inStockFlavors.length > 0 && (
                <div className="pdp__options">
                  <label className="pdp__options-label" htmlFor="flavor-select">
                    Flavor <span className="pdp__required">*</span>
                  </label>
                  <select
                    id="flavor-select"
                    className="pdp__select"
                    value={selectedFlavor}
                    onChange={(e) => setSelectedFlavor(e.target.value)}
                  >
                    <option value="" disabled>
                      Select a flavor
                    </option>
                    {inStockFlavors.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="pdp__qty-row">
                <button
                  type="button"
                  className="pdp__qty-btn"
                  aria-label="Decrease quantity"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                >
                  &minus;
                </button>
                <span className="pdp__qty-value">{qty}</span>
                <button
                  type="button"
                  className="pdp__qty-btn"
                  aria-label="Increase quantity"
                  onClick={() => setQty((q) => q + 1)}
                >
                  +
                </button>
              </div>

              {soldOut ? (
                <span className="pdp__sold-out">Out of Stock</span>
              ) : needsFlavor ? (
                <span className="pdp__add-btn pdp__add-btn--disabled" aria-disabled="true">
                  {inStockFlavors.length === 0 && hasFlavors
                    ? "All flavors are out of stock"
                    : "Select a flavor to continue"}
                </span>
              ) : (
                <button
                  type="button"
                  className={`pdp__add-btn ${added ? "pdp__add-btn--added" : ""}`}
                  onClick={handleAddToCart}
                >
                  {added ? "Added to Cart!" : "Add to Cart"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
